import {
  FormEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useLocation } from "react-router-dom";

import { Checkbox } from "@magnetic/checkbox";
import { Heading } from "@magnetic/heading";
import { Input } from "@magnetic/input";
import { Link } from "@magnetic/link";
import { Spinner } from "@magnetic/spinner";

import { CiscoLogo } from "src/common/ciscoLogo";
import { Button } from "src/components/buttons";
import { ErrorNotification } from "src/components/errorNotification";
import { HFlex } from "src/components/hFlex";
import { VFlex } from "src/components/vFlex";
import { Brig } from "src/gen/rest/brig/BrigService";
import { Fred } from "src/gen/rest/fred/FredService";
import { SignEulaRequest, SignEulaResponse } from "src/gen/schema/brig/bapi";
import { Theme } from "src/gen/schema/models/auth";
import { NewOrgRequest, NewOrgResponse } from "src/gen/schema/models/org";
import { Eula, SignUp, UserPreferences } from "src/gen/schema/models/user";
import { useForm } from "src/hooks";
import { Constants } from "src/utils/constants";
import { submitNewOrgViaFormPost } from "src/utils/crossGeoFormPost";
import { shouldUseUnspecifiedOrgGeo } from "src/utils/region/localGeo";
import { RegionSelect } from "src/utils/region/RegionSelect";
import {
  GeographicRegion,
  geographicRegionToGeo,
  getRegionInfo
} from "src/utils/region/regionTypes";
import {
  geographicRegionFromHostname,
  geographicRegionFromSessionContext
} from "src/utils/region/sessionGeo";

import { ApiError } from "./apiError";
import { ContactSales } from "./contactSales";
import {
  EULA_VERSION_FALLBACK,
  getSignupCrossGeoParams
} from "./crossGeoUrlParams";
import { LoginContext } from "./loginContext";

import "./welcomeScreen.scss";

const ERRORS = {
  PHONE: "Enter a valid phone number.",
  COUNTRY: "Select your country.",
  TOC: "Accept the terms and conditions to continue.",
  REGION: "Select a geographic region."
};

type SignUpFormFields = {
  email: string;
  orgName: string;
  toc: boolean;
  region: GeographicRegion | undefined;
};

const defaultFormValues: SignUpFormFields = {
  email: "",
  orgName: "",
  toc: false,
  region: undefined
};

export const WelcomeScreen = () => {
  const { session, logout, switchTenant, userContexts } =
    useContext(LoginContext);
  const location = useLocation();
  const [error, setError] = useState<string>();
  const { form, handleInputChange, handleFormValueChange, setForm } =
    useForm(defaultFormValues);
  const [timeZone] = useState<string>("Local");
  const [theme] = useState<number>(Theme.LIGHT);
  const [submitting, setSubmitting] = useState(false);
  const [autoSubmitting, setAutoSubmitting] = useState(false);
  const autoSubmitAttempted = useRef(false);
  const useUnspecifiedOrgGeo = shouldUseUnspecifiedOrgGeo();

  const crossGeoLanding = useMemo(
    () => getSignupCrossGeoParams(location.search),
    [location.search]
  );

  const hostRegion = useMemo(() => geographicRegionFromHostname(), []);

  const occupiedRegions = useMemo(() => {
    const regions = new Set<GeographicRegion>();
    if (userContexts?.contexts) {
      for (const ctx of userContexts.contexts) {
        const region = getRegionInfo(ctx.geo)?.code;
        if (region) {
          regions.add(region);
        }
      }
    }
    return regions;
  }, [userContexts]);

  useEffect(() => {
    if (session) {
      const crossGeo = crossGeoLanding;
      const regionFromRedirect = crossGeo
        ? getRegionInfo(crossGeo.geo)?.code
        : undefined;
      const regionFromCurrentOrg = geographicRegionFromSessionContext(
        session,
        userContexts
      );

      const allRegions = Object.values(GeographicRegion);
      const availableRegions = allRegions.filter(
        (r) => !occupiedRegions.has(r)
      );
      const onlyAvailable =
        availableRegions.length === 1 ? availableRegions[0] : undefined;

      setForm({
        email: session.email,
        orgName: crossGeo?.orgName || `${session.email} scratch`,
        toc: crossGeo !== null,
        region: useUnspecifiedOrgGeo
          ? undefined
          : regionFromRedirect ??
            regionFromCurrentOrg ??
            onlyAvailable ??
            hostRegion
      });
    }
  }, [
    session,
    setForm,
    userContexts,
    crossGeoLanding,
    hostRegion,
    occupiedRegions,
    useUnspecifiedOrgGeo
  ]);

  const submitNewOrg = useCallback(
    (
      orgName: string,
      email: string,
      eulaVersion: string,
      region: GeographicRegion | undefined
    ): Promise<NewOrgResponse | void> => {
      const submittedRegion = useUnspecifiedOrgGeo ? undefined : region;
      const targetGeo = geographicRegionToGeo(submittedRegion);
      const currentRegion =
        geographicRegionFromSessionContext(session, userContexts) ??
        geographicRegionFromHostname();
      const isCrossGeo =
        submittedRegion !== undefined &&
        currentRegion !== undefined &&
        submittedRegion !== currentRegion;

      if (isCrossGeo) {
        submitNewOrgViaFormPost(
          orgName,
          email,
          eulaVersion,
          targetGeo,
          timeZone,
          theme
        );
        return new Promise(() => {});
      }

      return Fred.newOrg(
        NewOrgRequest.create({
          signUp: SignUp.create({ companyName: orgName, email, eulaVersion }),
          userPreferences: UserPreferences.create({
            timezone: timeZone,
            theme
          }),
          targetGeo
        })
      );
    },
    [theme, timeZone, session, userContexts, useUnspecifiedOrgGeo]
  );

  useEffect(() => {
    if (
      !autoSubmitAttempted.current &&
      crossGeoLanding &&
      session?.requireSignUp &&
      !session?.federatedUser
    ) {
      autoSubmitAttempted.current = true;
      const { orgName, eulaVersion, geo } = crossGeoLanding;
      const region =
        getRegionInfo(geo)?.code ??
        geographicRegionFromSessionContext(session, userContexts);
      setAutoSubmitting(true);

      if (!useUnspecifiedOrgGeo && !region) {
        setAutoSubmitting(false);
        setError(ERRORS.REGION);
        return;
      }

      submitNewOrg(orgName, session!.email, eulaVersion, region).then(
        (resp) => {
          if (resp) {
            switchTenant(resp.id);
          }
        },
        (err: ApiError) => {
          setAutoSubmitting(false);
          setError(err.getMessage());
        }
      );
    }
  }, [
    session,
    userContexts,
    switchTenant,
    submitNewOrg,
    crossGeoLanding,
    useUnspecifiedOrgGeo
  ]);

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const eulaVersion = crossGeoLanding?.eulaVersion || EULA_VERSION_FALLBACK;
      const { toc, orgName, email } = form;

      if (!toc) {
        setError(ERRORS.TOC);
        return;
      }

      if (!useUnspecifiedOrgGeo && !session?.federatedUser && !form.region) {
        setError(ERRORS.REGION);
        return;
      }

      setSubmitting(true);

      if (session?.federatedUser) {
        const eula = Eula.create({
          email: email,
          eulaVersion
        });
        Brig.signEula(SignEulaRequest.create({ eula })).then(
          (resp: SignEulaResponse) => resp && switchTenant(resp.tenantId),
          (err: ApiError) => {
            setSubmitting(false);
            setError(err.getMessage());
          }
        );
        return;
      }

      submitNewOrg(orgName, email, eulaVersion, form.region).then(
        (resp) => {
          if (resp) {
            switchTenant(resp.id);
          }
        },
        (err: ApiError) => {
          setSubmitting(false);
          setError(err.getMessage());
        }
      );
    },
    [
      switchTenant,
      form,
      session,
      submitNewOrg,
      crossGeoLanding,
      useUnspecifiedOrgGeo
    ]
  );

  const handleTermsChange = useCallback(
    (e: FormEvent<HTMLButtonElement>) => {
      e.preventDefault();
      handleFormValueChange({ ...form, toc: !form.toc });
    },
    [form, handleFormValueChange]
  );

  const handleRegionChange = useCallback(
    (region: GeographicRegion | undefined) => {
      handleFormValueChange({ ...form, region });
    },
    [form, handleFormValueChange]
  );

  if (autoSubmitting) {
    return (
      <VFlex className="welcome" align="center" gap="xl">
        <VFlex gap="md" className="welcome-header" align="center">
          <Spinner />
        </VFlex>
      </VFlex>
    );
  }

  if (session?.requireSignUp || crossGeoLanding) {
    return (
      <VFlex className="welcome" align="center" gap="xl">
        <CiscoLogo />
        <VFlex gap="md" className="welcome-header" align="center">
          <Heading size="h1">Welcome to {Constants.APP_NAME}</Heading>
          <p>Provide the following information to begin.</p>
        </VFlex>
        <form onSubmit={onSubmit}>
          <VFlex gap="xl">
            <VFlex gap="md">
              <Input
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleInputChange}
                required={true}
                fixedWidth={true}
                autoComplete="off"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={true}
                disabled={true}
              />
              {!session?.federatedUser && (
                <>
                  <Input
                    label="Default organization name"
                    name="orgName"
                    onChange={handleInputChange}
                    value={form.orgName}
                    type="text"
                    size="md"
                    required={true}
                    fixedWidth={true}
                    autoComplete="off"
                  />
                  <RegionSelect
                    value={form.region}
                    onChange={handleRegionChange}
                    disabledValues={occupiedRegions}
                    disabled={useUnspecifiedOrgGeo}
                    includeUnspecified={useUnspecifiedOrgGeo}
                    required={!useUnspecifiedOrgGeo}
                  />
                </>
              )}
              <Checkbox onClick={handleTermsChange} checked={form.toc}>
                I agree to the
                <Link
                  onClick={(e) => e.stopPropagation()}
                  target="_blank"
                  href="https://www.cisco.com/c/en/us/about/legal/terms-conditions.html"
                >
                  terms and conditions
                </Link>
                of {Constants.APP_NAME}.
              </Checkbox>
              <ErrorNotification msg={error} />
            </VFlex>
            <VFlex gap="md">
              <Button
                type="submit"
                size="md"
                name="getstarted"
                className="save"
                loading={submitting}
                disabled={
                  submitting ||
                  !form.toc ||
                  !form.orgName ||
                  (!useUnspecifiedOrgGeo &&
                    !session?.federatedUser &&
                    !form.region)
                }
              >
                Get started
              </Button>
              <HFlex align="stretch" justify="center">
                <Button name="logout" kind="tertiary" onClick={logout}>
                  Back to login
                </Button>
              </HFlex>
            </VFlex>
          </VFlex>
        </form>
      </VFlex>
    );
  }
  return <ContactSales />;
};
